/**
 * nh-cart-delivery-refresh.js
 *
 * Rewrites the _delivery_estimate line item property whenever the cart is
 * rendered or mutated, and one last time before a standard cart-form checkout
 * submission. Prevents customers who return to a stale cart from checking out
 * with a past-dated delivery promise.
 *
 * A refresh may only ever move a delivery date LATER, never earlier. See the
 * monotonic guard in refreshDeliveryEstimates() — it is what keeps the cart,
 * the order in Shopify admin, and the order confirmation email agreeing with
 * the date the shopper was shown on the PDP.
 *
 * Reads two sibling properties written at ATC time by nh-delivery-bar.js:
 *   _delivery_transit_days — numeric days (e.g. "2")
 *   _delivery_label_tpl    — localized template containing "{date}"
 *
 * Hook points:
 *   DOMContentLoaded              — cart page + cart drawer first render
 *   cart:update event             — any cart mutation (add, qty change, remove)
 *   visibilitychange → visible    — tab regains focus after a long idle
 *   submit of form#cart-form      — final pass before the standard checkout redirect
 *
 * Note: the accelerated checkout buttons (Shop Pay, Apple Pay, etc.) bypass
 * our cart form entirely. They are protected by the page-load and cart:update
 * refreshes, which fire whenever the customer interacts with the site.
 */

// ── Business day math (mirrors nh-delivery-bar.js) ───────────────────────────

function easterSunday(y) {
  var a = y % 19, b = Math.floor(y / 100), c = y % 100;
  var d = Math.floor(b / 4), e = b % 4;
  var f = Math.floor((b + 8) / 25), g = Math.floor((b - f + 1) / 3);
  var h = (19 * a + b - d - g + 15) % 30;
  var i = Math.floor(c / 4), k = c % 4;
  var l = (32 + 2 * e + 2 * i - h - k) % 7;
  var m = Math.floor((a + 11 * h + 22 * l) / 451);
  var month = Math.floor((h + l - 7 * m + 114) / 31);
  var day   = ((h + l - 7 * m + 114) % 31) + 1;
  return [month, day];
}

function nthWeekday(year, month, weekday, n) {
  if (n > 0) {
    var first = new Date(year, month - 1, 1);
    return 1 + ((weekday - first.getDay() + 7) % 7) + (n - 1) * 7;
  }
  var last = new Date(year, month, 0);
  return last.getDate() - ((last.getDay() - weekday + 7) % 7);
}

function isHoliday(d) {
  var m = d.getMonth() + 1, day = d.getDate(), y = d.getFullYear();
  if (m === 1  && day === 1)  return true;
  if (m === 7  && day === 4)  return true;
  if (m === 7  && day === 14) return true;
  if (m === 12 && day === 25) return true;
  var easter = easterSunday(y);
  var eMon = new Date(y, easter[0] - 1, easter[1] + 1);
  if (d.getFullYear() === eMon.getFullYear() && d.getMonth() === eMon.getMonth() && d.getDate() === eMon.getDate()) return true;
  if (m === 1  && day === nthWeekday(y, 1,  1,  3)) return true;
  if (m === 2  && day === nthWeekday(y, 2,  1,  3)) return true;
  if (m === 5  && day === nthWeekday(y, 5,  1, -1)) return true;
  if (m === 9  && day === nthWeekday(y, 9,  1,  1)) return true;
  if (m === 11 && day === nthWeekday(y, 11, 4,  4)) return true;
  if (m === 11 && day === nthWeekday(y, 11, 4,  4) + 1) return true;
  return false;
}

// Pulls a date out of a legacy stored estimate like
//   "Delivery by Thursday, April 16"
//   "Livraison d'ici jeudi 16 avril"
//   "Complimentary delivery by April 6"
// Returns a Date pinned to midnight local time, or null if we can't parse it.
// Legacy items were written before we persisted transit days + label template,
// so this is the only signal we have that a stored estimate has gone stale.
function parseLegacyEstimateDate(estimate) {
  if (!estimate) return null;
  var months = {
    january: 0, february: 1, march: 2, april: 3, may: 4, june: 5,
    july: 6, august: 7, september: 8, october: 9, november: 10, december: 11,
    janvier: 0, fevrier: 1, 'f\u00e9vrier': 1, mars: 2, avril: 3, mai: 4, juin: 5,
    juillet: 6, aout: 7, 'ao\u00fbt': 7, septembre: 8, octobre: 9, novembre: 10,
    decembre: 11, 'd\u00e9cembre': 11
  };
  var rxMonthFirst = /([A-Za-z\u00c0-\u00ff]+)\s+(\d{1,2})/;
  var rxDayFirst   = /(\d{1,2})\s+([A-Za-z\u00c0-\u00ff]+)/;
  var monthName = null, day = null;
  var m = estimate.match(rxMonthFirst);
  if (m && months[m[1].toLowerCase()] !== undefined) {
    monthName = m[1]; day = parseInt(m[2], 10);
  } else {
    m = estimate.match(rxDayFirst);
    if (m && months[m[2].toLowerCase()] !== undefined) {
      monthName = m[2]; day = parseInt(m[1], 10);
    }
  }
  if (!monthName || isNaN(day) || day < 1 || day > 31) return null;
  var monthIdx = months[monthName.toLowerCase()];
  var now = new Date();
  var year = now.getFullYear();
  var candidate = new Date(year, monthIdx, day);
  candidate.setHours(0, 0, 0, 0);
  // If the current-year guess is wildly in the future (> 180 days), the label
  // almost certainly came from the previous calendar year. Pull it back so we
  // correctly flag "December 15" as past when today is "January 10".
  var today = new Date(); today.setHours(0, 0, 0, 0);
  if ((candidate.getTime() - today.getTime()) > (180 * 24 * 60 * 60 * 1000)) {
    candidate = new Date(year - 1, monthIdx, day);
    candidate.setHours(0, 0, 0, 0);
  }
  return candidate;
}

/**
 * Parse a YYYY-MM-DD settings string into a local Date, or null.
 * (Batch 22 — mirrors nh-delivery-bar.js.)
 * @param {string} s
 */
function parseNoShipDate(s) {
  var m = /^(\d{4})-(\d{2})-(\d{2})$/.exec((s || '').trim());
  if (!m) return null;
  var d = new Date(+m[1], +m[2] - 1, +m[3]);
  return isNaN(d.getTime()) ? null : d;
}

/**
 * Paris closure window from the cart-line snippet's data attributes
 * (values come from Theme settings; blank = no closure).
 */
function getParisNoShipWindow() {
  var node = document.querySelector('.nh-cart-line-delivery-estimate[data-paris-no-ship-start]');
  if (!node) return null;
  var start = parseNoShipDate(node.dataset.parisNoShipStart);
  var end   = parseNoShipDate(node.dataset.parisNoShipEnd);
  return (start && end && start <= end) ? { start: start, end: end } : null;
}

/**
 * Compute the delivery date as a Date pinned to local midnight.
 * Returns a Date (not a string) so callers can compare it against the date
 * already stored on the cart line. Mirrors nh-delivery-bar.js.
 * @param {number} transitDays
 * @param {{start: Date, end: Date} | null} [noShip]
 * @returns {Date}
 */
function getDeliveryDateObject(transitDays, noShip) {
  var now = new Date();
  var estString = now.toLocaleString('en-US', { timeZone: 'America/New_York' });
  var est = new Date(estString);
  var estHour = est.getHours();
  var estDay  = est.getDay();

  var fulfillDate = new Date(est.getFullYear(), est.getMonth(), est.getDate());
  var missedCutoff = estHour >= 15;

  if (estDay === 0) {
    fulfillDate.setDate(fulfillDate.getDate() + 1);
  } else if (estDay === 6) {
    fulfillDate.setDate(fulfillDate.getDate() + 2);
  } else if (estDay === 5 && missedCutoff) {
    fulfillDate.setDate(fulfillDate.getDate() + 3);
  } else if (missedCutoff) {
    fulfillDate.setDate(fulfillDate.getDate() + 1);
  }

  while (isHoliday(fulfillDate) || fulfillDate.getDay() === 0 || fulfillDate.getDay() === 6) {
    fulfillDate.setDate(fulfillDate.getDate() + 1);
  }

  // Warehouse closure window (Paris summer closure, Batch 22): if the
  // fulfillment date lands inside it, shipping resumes the first
  // business day after the window. Mirrors nh-delivery-bar.js.
  if (noShip && noShip.start && noShip.end && fulfillDate >= noShip.start && fulfillDate <= noShip.end) {
    fulfillDate = new Date(noShip.end.getTime());
    fulfillDate.setDate(fulfillDate.getDate() + 1);
    while (isHoliday(fulfillDate) || fulfillDate.getDay() === 0 || fulfillDate.getDay() === 6) {
      fulfillDate.setDate(fulfillDate.getDate() + 1);
    }
  }

  var deliverDate = new Date(fulfillDate.getTime());
  var count = 0;
  while (count < transitDays) {
    deliverDate.setDate(deliverDate.getDate() + 1);
    var dow = deliverDate.getDay();
    if (dow === 0) continue;
    var check = new Date(deliverDate.getFullYear(), deliverDate.getMonth(), deliverDate.getDate());
    if (isHoliday(check)) continue;
    count++;
  }

  return deliverDate;
}

/**
 * Render a Date as the localized "weekday, month day" string used inside the
 * delivery label. Locale comes from <html lang> on the cart view being shown,
 * which is what re-localizes a date that was stored in the other language.
 * @param {Date} date
 * @returns {string}
 */
function formatDeliveryDate(date) {
  var lang = document.documentElement.lang || 'en';
  return date.toLocaleDateString(lang, { weekday: 'long', month: 'long', day: 'numeric' });
}

// ── Refresh logic ────────────────────────────────────────────────────────────

var isRefreshing = false;

// Read the current locale's delivery prefixes from the cart-line snippet's
// data attributes. Falls back to the English defaults if the snippet hasn't
// rendered yet (e.g., empty cart on page load). Added 2026-05-05 to fix
// "Delivery by samedi 9 mai" mixed-locale strings: items added on /en
// persisted "Delivery by {date}" into _delivery_label_tpl, but on /fr/cart
// the date is recomputed in French while the prefix is still English. We
// re-prefix at refresh time using the locale active on this cart view.
function getCurrentLocalePrefixes() {
  var node = document.querySelector('.nh-cart-line-delivery-estimate[data-i18n-delivery-by]');
  if (!node) {
    return { deliveryBy: 'Delivery by', complimentaryDeliveryBy: 'Complimentary delivery by' };
  }
  return {
    deliveryBy:               node.dataset.i18nDeliveryBy            || 'Delivery by',
    complimentaryDeliveryBy:  node.dataset.i18nComplimentaryDeliveryBy || 'Complimentary delivery by',
  };
}

// Rewrite the prefix portion of a stored label template to the active
// locale's prefix. Templates always end in " {date}" so we can split on
// that sentinel. Returns the (possibly rewritten) template.
function relocalizeTemplate(tpl, prefixes) {
  if (!tpl || tpl.indexOf('{date}') === -1) return tpl;
  // Stored prefix is everything before "{date}", trimmed of the trailing
  // space. We don't need to know what locale wrote it — we just replace
  // that whole prefix segment with the current locale's equivalent. To
  // pick the right one we look for the "complimentary" sentinel keys
  // ("complimentary" in EN, "offerte" in FR) — both written by the same
  // i18n key path so any FR-ish marker tells us this was the
  // complimentary variant.
  var prefixSegment = tpl.replace('{date}', '').trim();
  var lower = prefixSegment.toLowerCase();
  var isComplimentary = lower.indexOf('complimentary') !== -1
                     || lower.indexOf('offerte') !== -1;
  var newPrefix = isComplimentary ? prefixes.complimentaryDeliveryBy : prefixes.deliveryBy;
  return newPrefix + ' {date}';
}

function cartRoute(key, fallback) {
  var themeRoutes = window.Theme && window.Theme.routes;
  return ((themeRoutes && themeRoutes[key]) || fallback) + '.js';
}

async function refreshDeliveryEstimates() {
  if (isRefreshing) return;
  isRefreshing = true;
  try {
    var res = await fetch(cartRoute('cart_url', '/cart'), { credentials: 'same-origin' });
    if (!res.ok) return;
    var cart = await res.json();
    if (!cart || !cart.items || !cart.items.length) return;

    var prefixes = getCurrentLocalePrefixes();

    for (var i = 0; i < cart.items.length; i++) {
      var item  = cart.items[i];
      var props = item.properties || {};

      var transit = parseInt(props._delivery_transit_days, 10);
      var tpl     = props._delivery_label_tpl;
      // Re-prefix the stored template to the locale active on this cart view.
      // No-op when the customer is on the same locale they added from.
      tpl = relocalizeTemplate(tpl, prefixes);

      // Legacy cart items were written before we persisted the inputs needed
      // to recompute the estimate (transit days + label template). We can't
      // regenerate a fresh date for them, but we CAN detect when the stored
      // label points at a date that has already passed, and clear it so the
      // cart never promises a delivery day that is in the past. Legacy items
      // with a stored date that is still today-or-future are left alone —
      // a variant change, quantity edit, or re-add will upgrade them to the
      // modern refresh path naturally.
      if (!transit || !tpl) {
        var legacyLabel = props._delivery_estimate;
        if (!legacyLabel) continue;
        var legacyDate = parseLegacyEstimateDate(legacyLabel);
        if (!legacyDate) continue;
        var today = new Date(); today.setHours(0, 0, 0, 0);
        if (legacyDate.getTime() >= today.getTime()) continue;
        // Past date → blank the property. Shopify's Liquid `!= blank` treats
        // empty strings as blank, so nh-cart-delivery-estimate.liquid will
        // simply not render the row instead of showing a stale promise.
        var clearedProps = Object.assign({}, props, { _delivery_estimate: '' });
        await fetch(cartRoute('cart_change_url', '/cart/change'), {
          method:  'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body:    JSON.stringify({ line: i + 1, properties: clearedProps }),
        });
        continue;
      }

      // 2026-07-16 (Camille): the closure window applies ONLY to lines
      // explicitly marked as shipping from Paris. The old default-Paris
      // fallback retro-applied the closure to legacy NY lines and
      // "messed up" every US cart. All pieces are stocked in both
      // warehouses (fulfillment rule), so under-applying the closure is
      // the correct failure mode.
      var lineNoShip = (props._delivery_warehouse === 'paris') ? getParisNoShipWindow() : null;
      var freshObj   = getDeliveryDateObject(transit, lineNoShip);

      // 2026-07-29 (Camille): monotonic guard — a refresh may only move a
      // delivery promise LATER, never earlier.
      //
      // The bug it fixes: the closure window above is applied only to lines
      // carrying _delivery_warehouse === 'paris'. That property did not exist
      // before 2026-07-17 and is never backfilled, so an older Paris line
      // recomputes here with noShip = null and lands weeks EARLIER than the
      // closure-aware date the PDP had already written — then overwrites it.
      // Because this refresher is the last writer before checkout, that wrong
      // date is what reached the order, the admin, and the confirmation email.
      // Order NH#732133 shipped "Monday, August 10" instead of
      // "Friday, August 28" for exactly this reason.
      //
      // Time only moves forward, so a legitimate refresh can only ever push a
      // date out (missed cutoff, new holiday, longer closure). An earlier
      // result is therefore always a regression — whatever caused it — so we
      // drop it and keep the stored date, re-rendered in the locale of the
      // cart view currently on screen.
      var storedObj = parseLegacyEstimateDate(props._delivery_estimate);
      if (storedObj && storedObj.getTime() > freshObj.getTime()) {
        freshObj = storedObj;
      }

      var freshLabel = tpl.replace('{date}', formatDeliveryDate(freshObj));
      if (freshLabel === props._delivery_estimate
          && tpl === props._delivery_label_tpl) continue;

      // Shopify's /cart/change.js replaces the properties object wholesale,
      // so we merge rather than overwrite. Persist the relocalized template
      // so subsequent refreshes start from the current-locale prefix.
      var newProps = Object.assign({}, props, {
        _delivery_estimate:  freshLabel,
        _delivery_label_tpl: tpl,
      });

      await fetch(cartRoute('cart_change_url', '/cart/change'), {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body:    JSON.stringify({ line: i + 1, properties: newProps }),
      });
    }
  } catch (_) {
    // Silent failure — never block checkout on a refresh error.
  } finally {
    isRefreshing = false;
  }
}

// ── Hooks ────────────────────────────────────────────────────────────────────

// WeakSet of forms we've already flushed, so the programmatic re-submit from
// inside our submit listener doesn't recurse back into the refresh path.
var flushedForms = new WeakSet();

function initHooks() {
  refreshDeliveryEstimates();

  document.addEventListener('cart:update', function() {
    refreshDeliveryEstimates();
  });

  document.addEventListener('visibilitychange', function() {
    if (document.visibilityState === 'visible') refreshDeliveryEstimates();
  });

  // Final safety net: catch standard cart-form checkout submissions and run
  // one more refresh before letting the form submit.
  // NH (2026-04-22, F-CartCTA): cart form id is unscoped "cart-form" per V65
  // (drawer + cart-page never coexist in DOM thanks to the template.name != 'cart'
  // guard in header-actions.liquid). Match by exact id OR class so any future
  // render context with class="cart-form" still triggers the refresh.
  //
  // IMPORTANT: use getAttribute('id') rather than form.id — HTMLFormElement has
  // a legacy named-access quirk where a child control with name="id" (Shopify
  // product forms always have <input name="id" value="{variant_id}">) shadows
  // the id IDL property. That made form.id return an HTMLInputElement on every
  // non-cart submit and threw "form.id.indexOf is not a function", surfacing
  // as broken CTAs in the cart drawer after ATC.
  document.addEventListener('submit', async function(e) {
    var form = e.target;
    if (!form || form.tagName !== 'FORM') return;
    var idAttr = form.getAttribute('id') || '';
    var isCartForm =
      idAttr === 'cart-form' ||
      idAttr.indexOf('cart-form') === 0 ||
      (form.classList && form.classList.contains('cart-form'));
    if (!isCartForm) return;
    if (flushedForms.has(form)) return;

    var submitter = e.submitter || null;
    var formaction = submitter && submitter.getAttribute ? submitter.getAttribute('formaction') : null;
    var triggeredByCheckout = submitter && (
      submitter.name === 'checkout' ||
      (formaction && formaction.indexOf('/checkout') !== -1)
    );
    if (!triggeredByCheckout) return;

    e.preventDefault();
    await refreshDeliveryEstimates();
    flushedForms.add(form);
    if (submitter && typeof submitter.click === 'function') submitter.click();
    else form.submit();
  }, true);
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initHooks);
} else {
  initHooks();
}
